import type React from 'react';
import { useEffect, useState, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { EmailSelectOption } from './EmailSelectOption';

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

interface EmailSearchInputProps<TFields extends FieldValues, Option extends { id: number | string; email: string }> {
  options: Option[];
  label: string;
  limitTags: number;
  name: Path<TFields>;
  control: Control<TFields>;
  onInputChange: (value: string) => void;
  filterOptions: (options: Option[], state: { inputValue: string }) => Option[];
}

export const EmailSearchInput = <TFields extends FieldValues, Option extends { id: number | string; email: string }>({
  options,
  label,
  limitTags,
  name,
  control,
  onInputChange,
  filterOptions,
}: EmailSearchInputProps<TFields, Option>) => {
  const [internalOptionValues, setInternalOptionValues] = useState<Option[]>(options);
  const [internalInputValue, setInternalInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (options.length > 0) {
      setInternalOptionValues(options);
    }
  }, [options]);

  const {
    field: { value, onChange: onChangeField },
  } = useController({
    name,
    control,
  });

  const handleAutocompleteOnChange: (
    event: React.SyntheticEvent,
    value: Array<Option>,
    reason: 'createOption' | 'selectOption' | 'removeOption' | 'blur' | 'clear',
    details?: string
  ) => void = (event, value) => {
    onChangeField(value);
  };

  const handleAddNewOptionOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      if (!isValidEmail(internalInputValue)) {
        control.setError(name, { message: 'Invalid field' });
        return;
      }

      const newOption = { email: internalInputValue } as Option;
      setInternalOptionValues([...internalOptionValues, newOption]);
      onChangeField([...(value || []), newOption]);
      setInternalInputValue('');

      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  return (
    <Autocomplete
      multiple
      limitTags={limitTags}
      renderOption={(props, option) => <EmailSelectOption key={option.id} props={props} option={option} />}
      id="multiple-limit-tags"
      options={internalOptionValues}
      filterOptions={filterOptions}
      filterSelectedOptions
      value={value ?? []}
      getOptionLabel={(option) => option.email}
      noOptionsText="No email found, press enter to add a new email"
      isOptionEqualToValue={(option, value) => option.id === value.id && option.email === value.email}
      onChange={(event, value, reason) => handleAutocompleteOnChange(event, value, reason)}
      onKeyDown={handleAddNewOptionOnEnter}
      renderTags={(value, getTagProps) => {
        return (value ?? [])?.map((option, index) => (
          <Chip avatar={<Avatar src={(option as any)?.avatar} />} {...getTagProps({ index })} key={index} label={option.email} />
        ));
      }}
      renderInput={(params) => {
        return (
          <TextField
            label={label}
            onChange={(event) => {
              setInternalInputValue(event.target.value);
              onInputChange(event.target.value);
            }}
            type="text"
            variant="outlined"
            inputRef={inputRef}
            {...params}
            sx={{
              '& .MuiFormHelperText-root ': {
                marginLeft: '0px !important',
              },
            }}
            fullWidth
          />
        );
      }}
    />
  );
};
